import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { activeDot, carouselStep, dotOffset, pageCount, perPageFrom, slideLabel } from '@theme/eme-grid-core';

class EmeGridDots extends Component {
  #track = null;
  #frame = 0;
  #step = 0;
  #maxScroll = 0;
  #resizeObserver = null;
  #count = 0;
  #paged = false;
  #perPage = 1;
  #pages = 0;
  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#paged = this.hasAttribute('data-paged');
    const track = this.previousElementSibling;
    if (!(track instanceof HTMLElement) || !track.hasAttribute('data-eme-grid')) return;
    this.#track = track;
    track.addEventListener('scroll', this.#handleScroll, { passive: true });
    track.addEventListener('focusin', this.#handleFocus);
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(track);
    const first = this.#items[0];
    if (first) this.#resizeObserver.observe(first);
    this.#measure();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#track?.removeEventListener('scroll', this.#handleScroll);
    this.#track?.removeEventListener('focusin', this.#handleFocus);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    cancelAnimationFrame(this.#frame);
  }

  get #items() {
    return [...(this.#track?.children ?? [])].filter(
      (child) => !child.matches('style, script') && getComputedStyle(child).display !== 'none'
    );
  }

  goTo(index) {
    const track = this.#track;
    if (!track) return;
    track.scrollTo({ left: dotOffset(Number(index), this.#step, this.#maxScroll), behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  previous() {
    this.goTo(Math.max(0, this.#index - 1));
  }

  next() {
    this.goTo(Math.min(this.#pages - 1, this.#index + 1));
  }

  #measure() {
    const track = this.#track;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const items = this.#items;
    this.#count = items.length;
    this.#maxScroll = track.scrollWidth - track.clientWidth;
    if (this.#paged) {
      this.#measurePages(items, gap);
    } else {
      this.#step = carouselStep(items.map((item) => item.offsetWidth), gap);
      this.hidden = this.#maxScroll <= 0;
    }
    this.#labelDots();
  }

  #measurePages(items, gap) {
    const itemWidth = items[0]?.getBoundingClientRect().width ?? 0;
    this.#perPage = perPageFrom(this.#track.clientWidth, itemWidth, gap);
    this.#pages = pageCount(items.length, this.#perPage);
    this.#step = this.#perPage * (itemWidth + gap);
    for (const [position, item] of items.entries()) {
      item.toggleAttribute('data-eme-page-start', position % this.#perPage === 0);
    }
    this.hidden = this.#pages <= 1;
  }

  #labelDots() {
    const template = this.dataset.label;
    const count = this.#paged ? this.#pages : this.#count;
    for (const [position, dot] of (this.refs.dots ?? []).entries()) {
      dot.hidden = position >= count;
      if (template) dot.setAttribute('aria-label', slideLabel(template, position + 1, count));
    }
  }

  #handleResize = () => {
    const firstVisible = this.#index * this.#perPage;
    this.#measure();
    if (this.#paged) {
      this.#index = Math.min(Math.floor(firstVisible / this.#perPage), Math.max(0, this.#pages - 1));
      this.#track.scrollTo({ left: dotOffset(this.#index, this.#step, this.#maxScroll), behavior: 'instant' });
    }
    this.#sync();
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  };

  #handleFocus = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches(':focus-visible')) return;
    const ordered = this.#items.sort((a, b) => a.offsetLeft - b.offsetLeft);
    const index = ordered.findIndex((item) => item.contains(target));
    if (index < 0) return;
    this.goTo(this.#paged ? Math.floor(index / this.#perPage) : index);
  };

  #sync() {
    const dots = this.refs.dots ?? [];
    const count = this.#paged ? this.#pages : Math.min(this.#count, dots.length);
    const index = activeDot({ scrollLeft: this.#track.scrollLeft, maxScroll: this.#maxScroll, step: this.#step, count });
    for (const [position, dot] of dots.entries()) dot.setAttribute('aria-current', String(position === index));
    if (this.#paged) this.#syncArrows(index);
  }

  #syncArrows(index) {
    this.#index = index;
    const { previous, next } = this.refs;
    if (!(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;
    const atStart = index === 0;
    const atEnd = index >= this.#pages - 1;
    if (atStart && document.activeElement === previous) next.focus();
    if (atEnd && document.activeElement === next) previous.focus();
    previous.disabled = atStart;
    next.disabled = atEnd;
  }
}

if (!customElements.get('eme-grid-dots')) customElements.define('eme-grid-dots', EmeGridDots);
